import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Gavel, Plus } from 'lucide-react';
import type { Asamblea } from '@condo/contracts';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useAsambleas } from '@/features/asambleas/api';
import { HistorialActas } from '@/features/asambleas/historial-actas';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/empty-state';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatShortDate } from '@/lib/format/date';
import type { ColumnDef } from '@tanstack/react-table';

// Activas = borrador | convocada | en_votacion | sin_quorum
const ESTADOS_ACTIVOS = ['borrador', 'convocada', 'en_votacion', 'sin_quorum'];
// Cerradas = cerrada | cerrada_sin_efecto | anulada
const ESTADOS_CERRADOS = ['cerrada', 'cerrada_sin_efecto', 'anulada'];

const variantMap: Record<Asamblea['estado'], 'neutral' | 'primary' | 'warning' | 'success' | 'danger'> = {
  borrador: 'neutral',
  convocada: 'primary',
  en_votacion: 'warning',
  sin_quorum: 'danger',
  cerrada: 'success',
  cerrada_sin_efecto: 'neutral',
  anulada: 'neutral',
};

const labelMap: Record<Asamblea['estado'], string> = {
  borrador: 'Borrador',
  convocada: 'Convocada',
  en_votacion: 'En votacion',
  sin_quorum: 'Sin quorum',
  cerrada: 'Cerrada',
  cerrada_sin_efecto: 'Cerrada sin efecto',
  anulada: 'Anulada',
};

const columns: ColumnDef<Asamblea>[] = [
  {
    accessorKey: 'tipo',
    header: 'Tipo',
    cell: ({ row }) => (
      <Badge variant="neutral" className="capitalize">
        {row.original.tipo}
      </Badge>
    ),
  },
  {
    accessorKey: 'fechaProgramada',
    header: 'Fecha programada',
    cell: ({ row }) => (
      <span className="font-display text-sm font-semibold">
        {formatShortDate(row.original.fechaProgramada)}
      </span>
    ),
  },
  {
    accessorKey: 'lugar',
    header: 'Lugar',
    cell: ({ row }) => (
      <span className="max-w-[200px] truncate text-sm text-[var(--color-ink-soft)]">
        {row.original.lugar}
      </span>
    ),
  },
  {
    accessorKey: 'materias',
    header: 'Materias',
    cell: ({ row }) => (
      <span className="tabular text-sm">{row.original.materias.length}</span>
    ),
  },
  {
    accessorKey: 'estado',
    header: 'Estado',
    cell: ({ row }) => (
      <Badge variant={variantMap[row.original.estado]}>
        {labelMap[row.original.estado]}
      </Badge>
    ),
  },
];

function AsambleasTabContent({
  condoId,
  estados,
  navigate,
}: {
  condoId: string;
  estados: string[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { data, isLoading } = useAsambleas(condoId);
  const items = (data?.items ?? []).filter((a) => estados.includes(a.estado));

  if (items.length === 0 && !isLoading) {
    return (
      <EmptyState
        icon={<Gavel className="h-5 w-5" />}
        title="Sin asambleas"
        description="No hay asambleas en esta categoria."
      />
    );
  }

  return (
    <DataTable
      columns={columns}
      data={items}
      isLoading={isLoading}
      onRowClick={(a) => navigate(`/app/condominios/${condoId}/asambleas/${a.id}`)}
    />
  );
}


export function AsambleasListRoute() {
  const { condoId, rol } = useCondoContext();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'activas' | 'cerradas' | 'historial'>('activas');

  const canCreate = rol === 'admin' || rol === 'comite' || rol === 'super_admin';

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Legal · Ley 21.442"
        title="Asambleas"
        subtitle="Convocatorias y actas de asambleas de copropietarios. Cada votacion queda registrada con alicuota ponderada."
        action={
          canCreate ? (
            <Button asChild>
              <Link to={`/app/condominios/${condoId}/asambleas/nueva`}>
                <Plus className="h-4 w-4" />
                Nueva asamblea
              </Link>
            </Button>
          ) : undefined
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList className="mb-6">
          <TabsTrigger value="activas">Activas</TabsTrigger>
          <TabsTrigger value="cerradas">Cerradas</TabsTrigger>
          <TabsTrigger value="historial">Historial de actas</TabsTrigger>
        </TabsList>

        <TabsContent value="activas">
          <AsambleasTabContent
            condoId={condoId}
            estados={ESTADOS_ACTIVOS}
            navigate={navigate}
          />
        </TabsContent>

        <TabsContent value="cerradas">
          <AsambleasTabContent
            condoId={condoId}
            estados={ESTADOS_CERRADOS}
            navigate={navigate}
          />
        </TabsContent>

        <TabsContent value="historial">
          <HistorialActas condoId={condoId} />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
